"use client";
import { MaterialImage, MaterialImageProps } from "@cc/material-components";
import { Button } from "@theme/components/typography.fontvariant";
import {
  Skeleton,
  Tab,
  tabClasses,
  TabProps,
  Tabs,
  tabsClasses,
  TabsProps,
  useScrollTrigger,
} from "@mui/material";
import { SyntheticEvent, useEffect, useState } from "react";
import {
  appbarHeight,
  appbarMotionTransition,
} from "@/layout/header/reactive-appbar";
import SectionWrapper, {
  basePx,
  SectionWrapperProps,
} from "@cc/section-wrapper";
import { sxArrayUtil } from "@util/sx-helpers";
import { useScrollSpy } from "@cc/scroll-spy";
import { useObservable } from "react-use";
import { SetOptional } from "type-fest";
import Link from "@cc/link";

export interface SectionNavigationProps
  extends Omit<SectionWrapperProps, "children"> {
  LogoImageProps?: SetOptional<MaterialImageProps, "width" | "height" | "alt">;
  TabProps?: Omit<TabProps, "children">;
  TabsProps?: Omit<TabsProps, "children">;
}

const SectionNavigation = (props: SectionNavigationProps) => {
  const {
    LogoImageProps,
    TabProps,
    TabsProps,
    SectionProps = {},
    ContainerProps = {},
    ...rest
  } = props;
  const { sx, ...restSectionProps } = SectionProps;
  const { sections, observer } = useScrollSpy();
  const activeNav = useObservable(observer, sections[0]?.navId);
  const [value, setValue] = useState<number>(0);
  const trigger = useScrollTrigger();

  const handleChange = (_e: SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (!activeNav) return;
    const idx = sections.findIndex((item) => item.href === `#${activeNav}`);
    if (idx === -1) return;
    setValue(idx);
  }, [sections, activeNav]);

  return (
    <SectionWrapper
      id={"scroll-spy-root"}
      width={1}
      direction={"row"}
      disablePassColorToChildren
      SectionProps={{
        bgcolor: "common.white",
        sx: [
          {
            position: "sticky",
            top: !trigger ? appbarHeight : 0,
            zIndex: 10,
            transition: `all ${appbarMotionTransition.duration}s ease-in-out`,
          },
          ...sxArrayUtil(sx),
        ],
        ...restSectionProps,
      }}
      ContainerProps={{
        py: 0,
        [LogoImageProps ? "pr" : "px"]: { ...basePx, xs: 0, md: 0 },
        alignItems: "center",
        height: 64,
        columnGap: { xs: 3, md: 4 },
        ...ContainerProps,
      }}
      {...rest}
    >
      {LogoImageProps && LogoImageProps.src && (
        <MaterialImage
          width={124}
          height={150}
          alt={"logo"}
          {...LogoImageProps}
          sx={[
            {
              width: { xs: 94, md: 124 },
              height: "100%",
              objectFit: "contain",
            },
            ...sxArrayUtil(LogoImageProps?.sx),
          ]}
        />
      )}
      {sections.length > 0 ? (
        <Tabs
          value={value}
          onChange={handleChange}
          variant={"scrollable"}
          scrollButtons={false}
          {...TabsProps}
          sx={[
            {
              width: 1,
              color: "grey.600",
              [`.${tabsClasses.flexContainer}`]: {
                justifyContent: "space-between",
                columnGap: 4,
              },
            },
            ...sxArrayUtil(TabsProps?.sx),
          ]}
        >
          {sections.map((item, index) => {
            const { navId, href } = item;
            return (
              <Tab
                key={`${navId}-${index}`}
                component={Link}
                disableLocale
                label={navId.replaceAll("-", " ")}
                href={href}
                disableRipple
                {...TabProps}
                sx={[
                  {
                    border: "none",
                    fontSize: Button,
                    px: 2,
                    height: { xs: 56, md: 64 },
                    color: "inherit",
                    fontWeight: 400,
                    opacity: 0.6,
                    [`&.${tabClasses.selected}`]: {
                      color: "primary.main",
                      opacity: 1,
                    },
                  },
                  ...sxArrayUtil(TabProps?.sx),
                ]}
              />
            );
          })}
        </Tabs>
      ) : (
        <Skeleton
          variant={"rectangular"}
          sx={{
            bgcolor: "secondary.800",
            height: { xs: 56, md: 64 },
            width: 1,
          }}
        />
      )}
    </SectionWrapper>
  );
};

export default SectionNavigation;
